import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import OrgChartTreeView from "./OrgChartTreeView";
import OrgChartListView from "./OrgChartListView";
import { fetchOrgChart as GetOrgChartService, fetchOrgChartNode } from "../../Domain/Services/GetOrgChartService";
import { getUser } from "../../../User/Domain/Services/GetUserService";
import ExtraTypeConfigListView from "../../../Extra/AfterSalesExtra/Application/Views/ExtraTypeConfigListView";
import { 
  Box, 
  Typography, 
  ToggleButton, 
  ToggleButtonGroup,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Autocomplete,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  Chip,
  Tabs,
  Tab,
  Tooltip
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getAuthenticatedAvatarUrl, useAvatarBlobSubscription } from "../../../Shared/Utils/Domain/Services/AvatarUrlService";
import { 
  PRIVATE_ADMIN_ORGCHART, 
  PRIVATE_ADMIN_ORGCHART_VACACIONES, 
  PRIVATE_ADMIN_ORGCHART_EXTRAS,
  PRIVATE_USER_ORGCHART,
  PRIVATE_USER_ORGCHART_VACACIONES,
  PRIVATE_USER_ORGCHART_EXTRAS
} from "../Constants/PathsConstant";
import { useAuthContext } from "Shared/Security/Domain/Contexts/authContext";
import { ADMIN_ORGCHART, ORGCHART } from "Shared/Security/Application/Constants/PermissionsConstant";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ListIcon from "@mui/icons-material/List";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import InfoIcon from "@mui/icons-material/Info";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import RefreshIcon from "@mui/icons-material/Refresh";

const BaseOrgChartView = ({ 
  chartType, 
  title, 
  description, 
  showFilters = false, 
  availableChartTypes = [], 
  onChartTypeChange = () => {},
  displayMode = "default" 
}) => {
  useAvatarBlobSubscription();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, checkPermission } = useAuthContext();
  const canAdminOrgChart = isAdmin() || checkPermission(ADMIN_ORGCHART) || checkPermission(ORGCHART);
  const isGeneral = (chartType || "").toUpperCase() === "GENERAL";
  const [viewType, setViewType] = useState("tree");
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [searchTargetId, setSearchTargetId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchProbe, setSearchProbe] = useState("");
  const [detailNode, setDetailNode] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [realUserPosition, setRealUserPosition] = useState(null);
  const [lazyLoadingNodes, setLazyLoadingNodes] = useState(new Set());
  const [initialCollapsedIds, setInitialCollapsedIds] = useState(new Set());
  const [collapsedIds, setCollapsedIds] = useState(new Set());
  
  const isSuperAdmin = isAdmin();
  let adminPath = isSuperAdmin ? PRIVATE_ADMIN_ORGCHART : PRIVATE_USER_ORGCHART;
  if (displayMode === "vacaciones") adminPath = isSuperAdmin ? PRIVATE_ADMIN_ORGCHART_VACACIONES : PRIVATE_USER_ORGCHART_VACACIONES;
  if (displayMode === "extras") adminPath = isSuperAdmin ? PRIVATE_ADMIN_ORGCHART_EXTRAS : PRIVATE_USER_ORGCHART_EXTRAS;

  const hasExtrasConfigPermission = isAdmin() || checkPermission('EXTRAS');
  const showConfigButton = displayMode === "extras" && hasExtrasConfigPermission;

  const searchResetTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (searchResetTimeoutRef.current) {
        clearTimeout(searchResetTimeoutRef.current);
      }
    };
  }, []);

  const loadData = useCallback((type) => {
    if (!type) {
      setAllData([]);
      return;
    }

    setLoading(true);
    setError(null);
    GetOrgChartService(type, {})
      .then(res => {
        setAllData(res || []);
        
        if (type.toUpperCase() === "GENERAL" && Array.isArray(res)) {
          const toCollapse = res
            .filter(node => node.level !== null && node.level >= 3)
            .map(node => node.id);
          const collapseSet = new Set(toCollapse);
          setInitialCollapsedIds(collapseSet);
          setCollapsedIds(collapseSet);
        } else {
          setInitialCollapsedIds(new Set());
          setCollapsedIds(new Set());
        }
      })
      .catch(err => {
        console.error("Error fetching org chart:", err);
        setError(t("orgChart.error.fetchError") || "No se pudo cargar el organigrama.");
        setAllData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [t]);

  useEffect(() => {
    loadData(chartType);
  }, [chartType, loadData]);

  const mergeNodesWithoutDuplicates = useCallback((incomingNodes) => {
    if (!Array.isArray(incomingNodes) || incomingNodes.length === 0) {
      return;
    }

    setAllData((prev) => {
      const byId = new Map(prev.map((node) => [node.id, node]));
      incomingNodes.forEach((node) => {
        const existing = byId.get(node.id) || {};
        byId.set(node.id, {
          ...existing,
          ...node,
        });
      });

      return Array.from(byId.values());
    });
  }, []);

  const loadNodeChildren = useCallback(async (node) => {
    if (!node || !node.id) {
      return;
    }

    const canLazyLoad = (chartType || "").toUpperCase() === "GENERAL";
    if (!canLazyLoad) {
      return;
    }

    const alreadyLoaded = allData.some((item) => item.parent === node.id);
    if (alreadyLoaded) {
      return;
    }

    setLazyLoadingNodes((prev) => new Set(prev).add(node.id));
    try {
      const resp = await fetchOrgChartNode(node.id, { maxDepth: 1, includeDetails: true });
      if (!resp) return;

      const normalizedNode = {
        id: resp.id,
        parent: resp.parentId ?? node.parent ?? null,
        text: resp.title || resp.chartType || "",
        chartType: resp.chartType,
        position: resp.position,
        positionId: resp.positionId,
        jobPositionId: resp.jobPositionId ?? null,
        isOfficialPosition: Boolean(resp.isOfficialPosition),
        user: resp.user,
        userId: resp.userId,
        userAvatar: resp.userAvatar,
        email: resp.email,
        phone: resp.phone,
        extension: resp.extension,
        hasChildren: Boolean(resp.hasChildren || (resp.children && resp.children.length > 0)),
      };

      const flattenedChildren = (resp.children || []).map((child) => ({
        id: child.id,
        parent: node.id,
        text: child.title || child.chartType || "",
        chartType: child.chartType,
        position: child.position,
        positionId: child.positionId,
        jobPositionId: child.jobPositionId ?? null,
        isOfficialPosition: Boolean(child.isOfficialPosition),
        user: child.user,
        userId: child.userId,
        userAvatar: child.userAvatar,
        email: child.email,
        phone: child.phone,
        extension: child.extension,
        hasChildren: Boolean(child.hasChildren || (child.children && child.children.length > 0)),
      }));

      mergeNodesWithoutDuplicates([normalizedNode, ...flattenedChildren]);
    } catch (err) {
      console.error("Error loading node children:", err);
    } finally {
      setLazyLoadingNodes((prev) => {
        const next = new Set(prev);
        next.delete(node.id);
        return next;
      });
    }
  }, [chartType, allData, mergeNodesWithoutDuplicates]);

  const generalExists = availableChartTypes.some((type) => (type || "").toUpperCase() === "GENERAL");
  const otherChartTypes = useMemo(() => {
    const list = [...new Set(availableChartTypes)]
      .filter((type) => type && type.toUpperCase() !== "GENERAL");
    if (chartType && chartType.toUpperCase() !== "GENERAL" && !list.some(t => (t || "").toUpperCase() === chartType.toUpperCase())) {
      list.push(chartType);
    }
    return list.sort();
  }, [availableChartTypes, chartType]);

  const selectChartTypeValue = useMemo(() => {
    if (!chartType) return "";
    if (chartType.toUpperCase() === "GENERAL") return "GENERAL";
    const match = otherChartTypes.find(t => (t || "").toUpperCase() === chartType.toUpperCase());
    return match || chartType;
  }, [chartType, otherChartTypes]);

  const chartNodeMap = useMemo(() => allData.reduce((acc, node) => {
    acc[node.id] = node;
    return acc;
  }, {}), [allData]);

  const rootNodes = allData.filter(node => node.parent === null || node.parent === 0 || !chartNodeMap[node.parent]);
  const rootIds = new Set(rootNodes.map(node => node.id));

  const delegationNodes = allData.filter(node => rootIds.has(node.parent));
  const isMaintenanceSelected = (chartType || "").toUpperCase() === "MANTENIMIENTO";
  const showDelegationTabs = isMaintenanceSelected && delegationNodes.length > 0;
  
  const [searchParams, setSearchParams] = useSearchParams();
  const delegationQuery = searchParams.get("delegation") || searchParams.get("delegationId");
  const [selectedDelegationId, setSelectedDelegationId] = useState(
    delegationQuery ? (Number(delegationQuery) || delegationQuery) : null
  );

  useEffect(() => {
    if (delegationQuery) {
      setSelectedDelegationId(Number(delegationQuery) || delegationQuery);
    }
  }, [delegationQuery]);

  const activeDelegationId = useMemo(() => {
    if (!showDelegationTabs) return false;
    const target = selectedDelegationId || delegationQuery;
    const match = delegationNodes.find(node => 
      String(node.id) === String(target) || 
      (node.text && String(target) && node.text.toLowerCase() === String(target).toLowerCase())
    );
    return match ? match.id : (delegationNodes[0]?.id ?? false);
  }, [showDelegationTabs, delegationNodes, selectedDelegationId, delegationQuery]);

  useEffect(() => {
    if (!showDelegationTabs) {
      if (!loading && (!isMaintenanceSelected || allData.length > 0)) {
        setSelectedDelegationId(null);
      }
      return;
    }
    const target = selectedDelegationId || delegationQuery;
    const match = delegationNodes.find(node => 
      String(node.id) === String(target) || 
      (node.text && String(target) && node.text.toLowerCase() === String(target).toLowerCase())
    );
    if (match) {
      if (String(selectedDelegationId) !== String(match.id)) {
        setSelectedDelegationId(match.id);
      }
    } else if (delegationNodes[0]) {
      setSelectedDelegationId(delegationNodes[0].id);
    }
  }, [showDelegationTabs, delegationNodes, selectedDelegationId, delegationQuery, loading, isMaintenanceSelected, allData.length]);

  const displayData = useMemo(() => {
    const currentId = activeDelegationId;
    if (!showDelegationTabs || !currentId) {
      return allData;
    }

    const allowedIds = new Set([currentId]);
    const queue = [currentId];

    while (queue.length > 0) {
      const current = queue.shift();
      allData.forEach((node) => {
        if (node.parent === current && !allowedIds.has(node.id)) {
          allowedIds.add(node.id);
          queue.push(node.id);
        }
      });
    }

    return allData.filter(node => allowedIds.has(node.id));
  }, [allData, activeDelegationId, showDelegationTabs]);

  const handleViewChange = (event, nextView) => {
    if (nextView !== null) {
      if (nextView === "config" && !hasExtrasConfigPermission) {
        return;
      }
      setViewType(nextView);
    }
  };

  const handleRefresh = () => {
    loadData(chartType);
  };

  const handleSearchChange = async (event, newValue) => {
    if (!newValue) {
      setSearchTargetId(null);
      return;
    }

    const currentChartType = (chartType || "").toUpperCase();
    const requiresPathLoading = currentChartType === "GENERAL";
    if (!requiresPathLoading) {
      setSearchTargetId(newValue.id);
      return;
    }

    const localById = new Map((allData || []).map((node) => [node.id, node]));
    const chain = [];
    let current = newValue;

    while (current && current.parent) {
      chain.push(current.parent);
      current = localById.get(current.parent);
    }

    const orderedAncestors = Array.from(new Set(chain.reverse()));
    const pendingMerge = [];

    if (orderedAncestors.length > 0) {
      setCollapsedIds(prev => {
        const next = new Set(prev);
        orderedAncestors.forEach(id => next.delete(id));
        return next;
      });
    }

    for (const ancestorId of orderedAncestors) {
      const childrenLoaded = Array.from(localById.values()).some((node) => node.parent === ancestorId);
      if (childrenLoaded) continue;

      try {
        const resp = await fetchOrgChartNode(ancestorId, { maxDepth: 1, includeDetails: true });
        if (!resp) continue;

        const normalizedAncestor = {
          id: resp.id,
          parent: resp.parentId,
          text: resp.title || resp.chartType || "",
          chartType: resp.chartType,
          position: resp.position,
          positionId: resp.positionId,
          user: resp.user,
          userId: resp.userId,
          userAvatar: resp.userAvatar,
          email: resp.email,
          phone: resp.phone,
          extension: resp.extension,
          hasChildren: Boolean(resp.hasChildren || (resp.children && resp.children.length > 0)),
        };

        const flattenedChildren = (resp.children || []).map((child) => ({
          id: child.id,
          parent: resp.id,
          text: child.title || child.chartType || "",
          chartType: child.chartType,
          position: child.position,
          positionId: child.positionId,
          user: child.user,
          userId: child.userId,
          userAvatar: child.userAvatar,
          email: child.email,
          phone: child.phone,
          extension: child.extension,
          hasChildren: Boolean(child.hasChildren || (child.children && child.children.length > 0)),
        }));

        const fetchedNodes = [normalizedAncestor, ...flattenedChildren];
        fetchedNodes.forEach((node) => {
          const existing = localById.get(node.id) || {};
          localById.set(node.id, { ...existing, ...node });
        });
        pendingMerge.push(...fetchedNodes);
      } catch (err) {
        console.error("Error loading ancestor path for search:", err);
      }
    }

    if (pendingMerge.length > 0) {
      mergeNodesWithoutDuplicates(pendingMerge);
    }

    setSearchTargetId(null);
    setTimeout(() => setSearchTargetId(newValue.id), 0);
  };

  const handleNodeClick = async (node) => {
    //por el momento dejar oculto, no tocar
    //if (!node.positionId && displayMode !== "vacaciones" && displayMode !== "extras") return; 
    setDetailNode(node);
    setRealUserPosition(null);
    setLoadingUserDetails(true);
    setIsDetailOpen(true);

    try {
      if (node.userId) {
        const user = await getUser(node.userId);
        setRealUserPosition(user.jobPosition?.name || node.position);
      } else {
        setRealUserPosition(node.position);
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      setRealUserPosition(node.position);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const normalizeSearchText = (value) => (value || "")
    .toString()
    .toLowerCase()
    .trim();

  const getOptionDisplayLabel = useCallback((option) => {
    const isPosition = !!option.positionId;
    return isPosition
      ? option.position + " (" + (option.user || "Vacante") + ")"
      : (option.text || "");
  }, []);

  const getOptionSearchText = useCallback((option) => {
    return normalizeSearchText(getOptionDisplayLabel(option));
  }, [getOptionDisplayLabel]);

  const filterSearchOptions = useCallback((options, state) => {
    const source = options || [];
    const inputValue = state && state.inputValue ? state.inputValue : "";
    const rawQuery = searchProbe || inputValue;
    const query = normalizeSearchText(rawQuery)
      .split(" ")
      .filter(Boolean)
      .join(" ");
    if (!query) return source;

    const queryTerms = query.split(" ").filter(Boolean);
    const matched = source.filter((option) => {
      const searchable = getOptionSearchText(option);
      return queryTerms.every((term) => searchable.includes(term));
    });

    return matched.sort((a, b) => {
      const aSearch = getOptionSearchText(a);
      const bSearch = getOptionSearchText(b);
      const aStarts = aSearch.startsWith(query) ? 1 : 0;
      const bStarts = bSearch.startsWith(query) ? 1 : 0;
      return bStarts - aStarts;
    });
  }, [getOptionSearchText, searchProbe]);

  const handleToggleNode = useCallback((id) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <>
      <Stack 
        direction={{ xs: "column", lg: "row" }} 
        justifyContent="space-between" 
        alignItems={{ xs: "flex-start", lg: "center" }} 
        spacing={3} 
        sx={{ mb: 4, borderBottom: "2px solid", borderColor: "primary.light", pb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: "bold", color: "primary.main", display: "flex", alignItems: "center", gap: 2 }}>
            <AccountTreeIcon fontSize="large" />
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>
        </Box>
        
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ width: { xs: "100%", lg: "auto" }, alignItems: "center" }}>
          <Autocomplete
            sx={{ width: 300 }}
            size="small"
            options={allData}
            disabled={!chartType}
            openOnFocus
            clearOnBlur={false}
            inputValue={searchInput}
            onInputChange={(event, value, reason) => {
              const nextValue = value || "";
              setSearchInput(nextValue);
              if (reason !== "input") {
                setSearchProbe(nextValue);
                return;
              }
              if (searchResetTimeoutRef.current) clearTimeout(searchResetTimeoutRef.current);
              const randomSuffix = String(Math.floor(Math.random() * 1000000));
              setSearchProbe(nextValue + randomSuffix);
              searchResetTimeoutRef.current = setTimeout(() => {
                setSearchProbe(nextValue);
                searchResetTimeoutRef.current = null;
              }, 0);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={filterSearchOptions}
            getOptionLabel={getOptionDisplayLabel}
            onChange={handleSearchChange}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={"Buscar persona o puesto..."} 
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2, bgcolor: "white" }
                }}
              />
            )}
          />

          {showFilters && (otherChartTypes.length > 0 || generalExists) && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="chart-type-select-label">Organigrama</InputLabel>
              <Select
                labelId="chart-type-select-label"
                value={selectChartTypeValue}
                label="Organigrama"
                onChange={(e) => onChartTypeChange(e.target.value)}
                sx={{ 
                  borderRadius: 2,
                  bgcolor: "white",
                  "& .MuiSelect-select": { py: 1.5 }
                }}
              >
                <MenuItem value="">
                  <Typography variant="body2" color="text.secondary">Seleccionar estructura...</Typography>
                </MenuItem>
                {generalExists && (
                  <MenuItem value="GENERAL">
                    <Typography variant="body2">GENERAL</Typography>
                  </MenuItem>
                )}
                {otherChartTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    <Typography variant="body2">{type}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={!chartType || loading}
            sx={{ borderRadius: 2, px: 2.5, py: 1, textTransform: "none", fontWeight: "bold" }}
          >
            Refrescar
          </Button>

          {canAdminOrgChart && (
            <Tooltip title={isGeneral ? "Escoge una estructura para gestionar" : "Gestionar estructura"}>
              <Button
                onClick={(e) => {
                  if (isGeneral) {
                    e.preventDefault();
                    toast.info("Escoge una estructura para gestionar. La vista GENERAL se monta a partir de ellas.");
                    return;
                  }
                  const params = new URLSearchParams();
                  if (chartType) {
                    params.set("structure", chartType);
                  }
                  if (showDelegationTabs && activeDelegationId) {
                    params.set("delegation", activeDelegationId);
                  }
                  const queryString = params.toString();
                  navigate(queryString ? `${adminPath}?${queryString}` : adminPath);
                }}
                variant="outlined"
                color={isGeneral ? "inherit" : "primary"}
                startIcon={<SettingsIcon />}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: "bold",
                  ...(isGeneral && {
                    borderColor: '#cbd5e1',
                    color: '#64748b',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      bgcolor: '#f1f5f9',
                    },
                  }),
                }}
              >
                Gestionar
              </Button>
            </Tooltip>
          )}

          <ToggleButtonGroup
            value={viewType}
            exclusive
            onChange={handleViewChange}
            aria-label="tipo de vista"
            color="primary"
            sx={{ bgcolor: "white", "& .MuiToggleButton-root": { px: 2, py: 1 } }}
          >
            <ToggleButton value="tree" aria-label="vista de arbol">
              <AccountTreeIcon sx={{ mr: 1 }} />
              Arbol
            </ToggleButton>
            <ToggleButton value="list" aria-label="vista de lista">
              <ListIcon sx={{ mr: 1 }} />
              Lista
            </ToggleButton>
            {showConfigButton && (
              <ToggleButton value="config" aria-label="configuración">
                <SettingsIcon sx={{ mr: 1 }} />
                Configuración
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: "center", p: 10 }}>
          <Typography variant="h6" color="error">{error}</Typography>
        </Box>
      ) : !chartType ? (
        <Box sx={{ textAlign: "center", p: 10 }}>
          <Typography variant="h6" color="text.secondary">Selecciona una estructura para visualizar el organigrama.</Typography>
        </Box>
      ) : allData.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 8, bgcolor: "white", borderRadius: 2, border: "1px dashed", borderColor: "divider" }}>
            <Typography variant="h6" color="text.secondary">
              No hay nodos en esta estructura.
            </Typography>
          </Box>
      ) : (
        <Box sx={{ mt: 2, overflow: "hidden", width: "100%", pb: 4 }}>
          {showDelegationTabs && (
            <Box sx={{ mb: 2, bgcolor: "#eaf6fb", borderRadius: 2, px: 1, py: 0.5, border: "1px solid #c8e4f0" }}>
              <Tabs
                value={activeDelegationId}
                onChange={(e, val) => {
                  if (val) {
                    setSelectedDelegationId(val);
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set("delegation", val);
                    setSearchParams(newParams, { replace: true });
                  }
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 44,
                  "& .MuiTabs-indicator": { height: 3 },
                  "& .MuiTab-root": {
                    textTransform: "none",
                    minHeight: 40,
                    fontWeight: 600,
                    color: "text.primary"
                  }
                }}
              >
                {delegationNodes.map((delegation) => (
                  <Tab key={delegation.id} label={delegation.text} value={delegation.id} />
                ))}
              </Tabs>
            </Box>
          )}

          {viewType === "tree" ? (
            <OrgChartTreeView 
              data={displayData} 
              onNodeClick={handleNodeClick}
              targetNodeId={searchTargetId}
              onExpandNode={loadNodeChildren}
              loadingNodeIds={lazyLoadingNodes}
              initialCollapsedIds={initialCollapsedIds}
              collapsedIds={collapsedIds}
              onToggleNode={handleToggleNode}
              displayMode={displayMode}
            />
          ) : viewType === "list" ? (
            <OrgChartListView 
              data={displayData} 
              onNodeClick={handleNodeClick}
              highlightNodeId={searchTargetId}
              onExpandNode={loadNodeChildren}
              loadingNodeIds={lazyLoadingNodes}
              initialCollapsedIds={initialCollapsedIds}
              collapsedIds={collapsedIds}
              onToggleNode={handleToggleNode}
              displayMode={displayMode}
            />
          ) : viewType === "config" && hasExtrasConfigPermission ? (
            <ExtraTypeConfigListView />
          ) : null}
        </Box>
      )}

      <Dialog 
        open={isDetailOpen} 
        onClose={() => setIsDetailOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: "primary.main", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>Detalles del Nodo</Typography>
          <InfoIcon />
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3, pb: 4 }}>
          {detailNode && (
            <Stack spacing={3} alignItems="center">
              <Avatar 
                src={getAuthenticatedAvatarUrl(detailNode.userAvatar)} 
                sx={{ width: 100, height: 100, border: "4px solid", borderColor: "primary.light", boxShadow: 3 }}
              >
                <PersonIcon sx={{ fontSize: 60 }} />
              </Avatar>

              <Box sx={{ textAlign: "center", width: "100%" }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1, textTransform: "uppercase" }}>
                  {loadingUserDetails ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : (
                    realUserPosition || (detailNode.positionId ? detailNode.position : detailNode.text)
                  )}
                </Typography>
                
                {detailNode.user && (
                  <Chip 
                    label={detailNode.user} 
                    color="primary" 
                    variant="outlined" 
                    sx={{ fontWeight: "bold", px: 2 }} 
                  />
                )}
              </Box>

              <Divider sx={{ width: "100%" }} />

              <Stack spacing={2} sx={{ width: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <BusinessIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Estructura</Typography>
                    <Typography variant="body1">{chartType}</Typography>
                  </Box>
                </Box>

                {detailNode.email && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <EmailIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Correo Electronico</Typography>                      <Typography variant="body2" sx={{ wordBreak: "break-all", fontWeight: "medium" }}>
                        {detailNode.email}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {detailNode.extension && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <PhoneIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Contacto Directo</Typography>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {"Ext. " + detailNode.extension}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#f8fafd" }}>
          <Button onClick={() => setIsDetailOpen(false)} variant="contained" fullWidth sx={{ borderRadius: 2, height: 45 }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

BaseOrgChartView.propTypes = {
  chartType: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  showFilters: PropTypes.bool,
  availableChartTypes: PropTypes.arrayOf(PropTypes.string),
  onChartTypeChange: PropTypes.func,
  displayMode: PropTypes.string
};

export default BaseOrgChartView;